import React from "react";
import "../App.css";
import i18next from "../i18n";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import FeatureCards from "../components/FeatureCards";
import SubLogo from "../components/SubLogo";
import MigratorFlow from "./components/MigratorFlow";

function Migrator() {
  const navigate = useNavigate();
  const challenges = i18next.t("pages.migrator.challenges.items", {
    returnObjects: true,
  });
  const strengths = i18next.t("pages.migrator.expertise.items", {
    returnObjects: true,
  });

  return (
    <div className="min-h-screen bg-white text-main-dark">
      <Helmet>
        <title>{i18next.t("pages.migrator.title")}</title>
        <meta
          name="description"
          content={i18next.t("pages.migrator.description")}
        />
        <link rel="canonical" href="https://xperconsulting.no/migrator" />
      </Helmet>

      <section className="overflow-hidden bg-white">
        <div className="container max-w-xl justify-start pb-16 pt-36 text-main-dark">
          <div className="w-full text-left md:w-3/5">
            <h1 className="sr-only">{i18next.t("pages.migrator.title")}</h1>
            <SubLogo product="Migrator" />
            <p className="mb-3 text-xl font-semibold leading-relaxed text-main-dark">
              {i18next.t("pages.migrator.hero.title")}
            </p>
            <p className="mb-0 max-w-[58ch] text-lg leading-relaxed text-secondary">
              {i18next.t("pages.migrator.description")}
            </p>
          </div>
        </div>
      </section>

      <FeatureCards
        title="pages.migrator.services.title"
        items={[
          { icon: "migration", key: "pages.migrator.services.volume", tone: "warm", duotone: true },
          { icon: "modernization", key: "pages.migrator.services.modernize", tone: "warm", duotone: true },
          { icon: "archivePlatform", key: "pages.migrator.services.ibm", tone: "warm", duotone: true },
        ]}
      />

      <section className="bg-gray py-20 md:py-28" aria-labelledby="migration-context-title">
        <div className="container max-w-xl grid gap-12 text-left md:grid-cols-2 md:gap-20">
          <div>
            <h2 id="migration-context-title" className="mb-4 text-3xl font-bold leading-tight tracking-[-0.025em] md:text-4xl">
              {i18next.t("pages.migrator.challenges.title")}
            </h2>
            <p className="mb-7 max-w-[56ch] text-lg leading-relaxed text-secondary">
              {i18next.t("pages.migrator.challenges.description")}
            </p>
            <ul className="mb-0 space-y-4">
              {Array.isArray(challenges) && challenges.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-main-dark">
                  <span className="mt-[0.6rem] h-2 w-2 shrink-0 rounded-full bg-contrast" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="mb-4 text-3xl font-bold leading-tight tracking-[-0.025em] md:text-4xl">
              {i18next.t("pages.migrator.expertise.title")}
            </h2>
            <p className="mb-7 max-w-[56ch] text-lg leading-relaxed text-secondary">
              {i18next.t("pages.migrator.expertise.description")}
            </p>
            <ul className="mb-0 space-y-4">
              {Array.isArray(strengths) && strengths.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-main-dark">
                  <span className="mt-[0.6rem] h-2 w-2 shrink-0 rounded-full bg-contrast" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="container max-w-xl">
          <MigratorFlow />
        </div>
      </section>

      <section className="bg-main py-20 md:py-28" aria-label={i18next.t("pages.migrator.proof.label")}>
        <div className="container max-w-lg">
          <div className="grid gap-8 text-center sm:grid-cols-2 md:gap-16">
              <div className="flex min-h-[13rem] min-w-0 flex-col items-center justify-center rounded-2xl bg-gray px-6 py-10 md:min-h-[15rem] md:px-10">
                <p className="mb-2 text-5xl font-bold leading-none text-contrast md:text-6xl">20+</p>
                <p className="mx-auto mb-0 max-w-[18ch] text-sm font-medium leading-snug text-main-dark md:text-base">
                  {i18next.t("pages.migrator.proof.years")}
                </p>
                <p className="mx-auto mb-0 mt-5 max-w-[32ch] text-sm leading-relaxed text-secondary">
                  {i18next.t("pages.migrator.proof.yearsDescription")}
                </p>
              </div>
              <div className="flex min-h-[13rem] min-w-0 flex-col items-center justify-center rounded-2xl bg-gray px-6 py-10 md:min-h-[15rem] md:px-10">
                <p className="mb-2 whitespace-nowrap text-5xl font-bold leading-none text-contrast md:text-6xl">1 mrd.+</p>
                <p className="mx-auto mb-0 max-w-[18ch] text-sm font-medium leading-snug text-main-dark md:text-base">
                  {i18next.t("pages.migrator.proof.documents")}
                </p>
                <p className="mx-auto mb-0 mt-5 max-w-[32ch] text-sm leading-relaxed text-secondary">
                  {i18next.t("pages.migrator.proof.documentsDescription")}
                </p>
              </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-20 text-white md:py-24">
        <div className="container max-w-lg flex flex-col gap-8 text-left md:flex-row md:items-center md:justify-between md:gap-16">
          <div>
            <h2 className="mb-3 text-3xl font-bold leading-tight tracking-[-0.025em] text-white md:text-4xl">
              {i18next.t("pages.migrator.cta.title")}
            </h2>
            <p className="mb-0 max-w-[58ch] text-lg leading-relaxed text-white/85">
              {i18next.t("pages.migrator.cta.description")}
            </p>
          </div>
          <Button color="white" size="large" className="shrink-0" onClick={() => navigate("/skjema/kontakt-oss")}>
            {i18next.t("pages.migrator.cta.button")}
          </Button>
        </div>
      </section>
    </div>
  );
}

export default Migrator;
